import { TAP_THRESHOLD_MS } from "@/drawlock/drawLock";
import { useHoldKey } from "@/drawlock/useHoldKey";

type UseHoldToTalkArgs = {
  // Hook is a no-op when false. Pass `aiMode === "voice"` from the caller so
  // PTT only activates in voice mode.
  enabled: boolean;
  // Called once the key has been held past the threshold: opens a new voice
  // turn before audio capture starts.
  startVoiceTurn: () => void;
  // Called right after startVoiceTurn — begins mic capture. Resolving false
  // means capture never started, and the turn just opened must be finalized.
  startRecording: () => void | boolean | Promise<void | boolean>;
  // Called on release; finalizeAudio is invoked once stopRecording resolves.
  stopRecording: () => Promise<unknown>;
  finalizeAudio: () => void;
  // Optimistic capture: called on key-down to start buffering audio locally
  // before tap-vs-hold is known (the turn still waits for the threshold).
  armRecording?: () => void;
  // Called when the press turns out to be a tap — discards the pre-roll.
  abortArmedRecording?: () => void;
};

/** Push-to-talk lives on S; the space bar now locks the canvas for drawing. */
const TALK_KEY_CODE = "KeyS";

/**
 * Global hold-to-talk on the S key, so the student can speak from anywhere
 * (canvas, agent panel, captions).
 *
 * Press bookkeeping lives in the shared `useHoldKey`. This hook is only the
 * spaces wiring: a voice turn opens on hold, and a tap does nothing because
 * spaces has no submission state.
 */
export function useHoldToTalk({
  enabled,
  startVoiceTurn,
  startRecording,
  stopRecording,
  finalizeAudio,
  armRecording,
  abortArmedRecording,
}: UseHoldToTalkArgs): void {
  useHoldKey({
    code: TALK_KEY_CODE,
    thresholdMs: TAP_THRESHOLD_MS,
    enabled,
    onArm: armRecording,
    onAbortArm: abortArmedRecording,
    onHoldStart: () => {
      startVoiceTurn();
      // The gateway is listening from here on. If capture never starts, close
      // the turn immediately — otherwise it waits on audio that never comes —
      // and report the failure so no release is expected for this press.
      const handle = (started: void | boolean) => {
        if (started === false) {
          finalizeAudio();
          return false;
        }
        return true;
      };
      // Stay synchronous when the mic does: only a genuinely async start should
      // open an in-flight window in the shared machine.
      const started = startRecording();
      return started instanceof Promise
        ? started.then(handle)
        : handle(started);
    },
    onHoldEnd: () => {
      // Finalize even if the stop itself rejects — an unfinalized voice turn
      // leaves the gateway waiting forever.
      void stopRecording().then(finalizeAudio, finalizeAudio);
    },
    // Spaces has no tap action: a stray tap is simply a no-op.
  });
}
